import React from 'react'
import { XIcon, MapPinIcon, ExternalLinkIcon } from 'lucide-react'
import { formatAccuracyM, formatCoordinates, googleMapsUrl } from '../../lib/geoDisplay'

export type PunchDetailsEntry = {
  id: string
  employee_id: string | null
  job_id: string | null
  entry_type?: string | null
  clock_in_time: string | null
  clock_out_time: string | null
  total_hours: number | null
  is_approved: boolean | null
  approved_by: string | null
  approved_at: string | null
  notes: string | null
  clock_in_latitude?: number | null
  clock_in_longitude?: number | null
  clock_out_latitude?: number | null
  clock_out_longitude?: number | null
  clock_in_accuracy_m?: number | null
  clock_out_accuracy_m?: number | null
  clock_in_distance_ft?: number | null
  clock_out_distance_ft?: number | null
  created_at?: string | null
  updated_at?: string | null
  employee?: { first_name?: string; last_name?: string } | null
  job?: {
    title?: string
    job_number?: string
    operational_division_id?: string | null
    service_type?: string | null
  } | null
}

interface PunchDetailsModalProps {
  entry: PunchDetailsEntry | null
  onClose: () => void
  onEditTimes?: (entry: PunchDetailsEntry) => void
}

function empName(e?: { first_name?: string; last_name?: string } | null) {
  if (!e) return '—'
  return `${e.first_name || ''} ${e.last_name || ''}`.trim() || '—'
}

function fmt(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

function typeLabel(t?: string | null) {
  if (t === 'travel') return 'Travel'
  if (t === 'day') return 'Day clock'
  if (t === 'job') return 'Job'
  return t || '—'
}

function typeBadgeClass(t?: string | null) {
  if (t === 'travel') return 'bg-purple-100 text-purple-800'
  if (t === 'day') return 'bg-blue-100 text-blue-800'
  if (t === 'job') return 'bg-emerald-100 text-emerald-800'
  return 'bg-gray-100 text-gray-700'
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100 last:border-0">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="col-span-2 text-sm text-gray-900 break-words">{children}</dd>
    </div>
  )
}

export function PunchDetailsModal({ entry, onClose, onEditTimes }: PunchDetailsModalProps) {
  if (!entry) return null

  const inMaps = googleMapsUrl(entry.clock_in_latitude, entry.clock_in_longitude)
  const outMaps = googleMapsUrl(entry.clock_out_latitude, entry.clock_out_longitude)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Punch details</h2>
            <p className="text-sm text-gray-500">{empName(entry.employee)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-1">
          <DetailRow label="Type">
            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${typeBadgeClass(entry.entry_type)}`}>
              {typeLabel(entry.entry_type)}
            </span>
          </DetailRow>
          <DetailRow label="Job">
            {entry.job?.title || entry.job?.job_number || (entry.entry_type === 'travel' ? 'Between jobs' : '—')}
          </DetailRow>
          <DetailRow label="Clock in">{fmt(entry.clock_in_time)}</DetailRow>
          <DetailRow label="Clock out">{fmt(entry.clock_out_time)}</DetailRow>
          <DetailRow label="Hours">
            {entry.total_hours != null ? `${Number(entry.total_hours).toFixed(2)}h` : '—'}
          </DetailRow>
          <DetailRow label="Approval">
            {entry.is_approved ? (
              <span>
                Approved{entry.approved_at ? ` · ${fmt(entry.approved_at)}` : ''}
              </span>
            ) : (
              'Pending'
            )}
          </DetailRow>
          <DetailRow label="Notes">{entry.notes?.trim() || '—'}</DetailRow>

          <div className="pt-3 pb-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Clock-in location</p>
            <DetailRow label="GPS">
              <span className="inline-flex items-center gap-2">
                <MapPinIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                {formatCoordinates(entry.clock_in_latitude, entry.clock_in_longitude)}
                {inMaps && (
                  <a
                    href={inMaps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-blue-600 hover:underline text-xs"
                  >
                    Map <ExternalLinkIcon className="w-3 h-3" />
                  </a>
                )}
              </span>
            </DetailRow>
            <DetailRow label="Accuracy">{formatAccuracyM(entry.clock_in_accuracy_m)}</DetailRow>
            <DetailRow label="Distance to site">
              {entry.clock_in_distance_ft != null ? `${Math.round(Number(entry.clock_in_distance_ft))} ft` : '—'}
            </DetailRow>
          </div>

          <div className="pt-2 pb-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Clock-out location</p>
            <DetailRow label="GPS">
              <span className="inline-flex items-center gap-2">
                <MapPinIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                {formatCoordinates(entry.clock_out_latitude, entry.clock_out_longitude)}
                {outMaps && (
                  <a
                    href={outMaps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-blue-600 hover:underline text-xs"
                  >
                    Map <ExternalLinkIcon className="w-3 h-3" />
                  </a>
                )}
              </span>
            </DetailRow>
            <DetailRow label="Accuracy">{formatAccuracyM(entry.clock_out_accuracy_m)}</DetailRow>
            <DetailRow label="Distance to site">
              {entry.clock_out_distance_ft != null ? `${Math.round(Number(entry.clock_out_distance_ft))} ft` : '—'}
            </DetailRow>
          </div>

          <DetailRow label="Entry ID">
            <span className="font-mono text-xs text-gray-500">{entry.id}</span>
          </DetailRow>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
          {onEditTimes && (
            <button
              type="button"
              onClick={() => onEditTimes(entry)}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Edit times
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default PunchDetailsModal